import { CrmSocialPlatformIcon } from "@/components/crm/CrmSocialPlatformIcon";
import {
  displayCrmEmailTypeLabel,
  formatCrmPhoneDisplay,
  formatCrmPhoneTypeLabel,
  formatCrmSocialPlatformLabel,
  type CrmChannelTypeOptions,
  type CrmContactChannelsState,
  type CrmContactEmailContext,
} from "@/lib/crmContactChannels";
import {
  displayCrmAddressTypeLabel,
  formatCrmAddressOneLine,
  type CrmAddressTypeOptions,
} from "@/lib/crmContactAddresses";

type CrmContactChannelsDisplayProps = {
  channels: CrmContactChannelsState;
  emailContext?: CrmContactEmailContext;
  typeOptions?: CrmChannelTypeOptions;
  addressTypeOptions?: CrmAddressTypeOptions;
  emptyMessage?: string;
};

function PrimaryBadge() {
  return (
    <span className="ml-2 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-100">
      Primary
    </span>
  );
}

export function CrmContactChannelsDisplay({
  channels,
  emailContext = "contact",
  typeOptions,
  addressTypeOptions,
  emptyMessage = "No channels on file.",
}: CrmContactChannelsDisplayProps) {
  const emails = channels.emails.filter((e) => e.address.trim());
  const phones = channels.phones.filter((p) => p.number.trim());
  const socials = channels.socials.filter((s) => s.url.trim());
  const addresses = channels.addresses.filter((a) => formatCrmAddressOneLine(a));

  const hasAny = emails.length + phones.length + socials.length + addresses.length > 0;
  if (!hasAny) {
    return <p className="text-sm text-gray-500">{emptyMessage}</p>;
  }

  return (
    <div className="space-y-4 text-sm">
      {emails.length ? (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Email</h4>
          <ul className="mt-2 space-y-2">
            {emails.map((row, index) => (
              <li key={`email-${index}`} className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-gray-500">
                  {displayCrmEmailTypeLabel(
                    typeOptions?.emailLabels[row.type],
                    row.type,
                    emailContext,
                  )}
                  :
                </span>
                <a href={`mailto:${row.address.trim()}`} className="font-medium text-indigo-700 hover:underline dark:text-indigo-300">
                  {row.address.trim()}
                </a>
                {row.isPrimary ? <PrimaryBadge /> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {phones.length ? (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Phone</h4>
          <ul className="mt-2 space-y-2">
            {phones.map((row, index) => (
              <li key={`phone-${index}`} className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-gray-500">{formatCrmPhoneTypeLabel(row.type)}:</span>
                <span className="font-medium">{formatCrmPhoneDisplay(row)}</span>
                {row.smsOptIn ? (
                  <span className="text-xs text-gray-500">SMS opt-in</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {socials.length ? (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Social</h4>
          <ul className="mt-2 space-y-2">
            {socials.map((row, index) => {
              const url = row.url.trim();
              const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
              return (
                <li key={`social-${index}`} className="flex flex-wrap items-center gap-2">
                  <CrmSocialPlatformIcon
                    platform={row.platform}
                    iconUrl={typeOptions?.socialIconUrls[row.platform]}
                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-300 bg-white dark:border-white/20 dark:bg-black/20"
                    title={formatCrmSocialPlatformLabel(row.platform)}
                  />
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className="break-all font-medium text-indigo-700 hover:underline dark:text-indigo-300"
                  >
                    {url}
                  </a>
                  {row.isPrimary ? <PrimaryBadge /> : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {addresses.length ? (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Address</h4>
          <ul className="mt-2 space-y-2">
            {addresses.map((row, index) => (
              <li key={`address-${index}`}>
                <p className="font-medium text-gray-800 dark:text-gray-100">
                  {displayCrmAddressTypeLabel(addressTypeOptions?.addressLabels[row.type], row.type)}
                  {row.isPrimary ? <PrimaryBadge /> : null}
                </p>
                <p className="text-gray-700 dark:text-gray-300">{formatCrmAddressOneLine(row)}</p>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
